import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { APP_NAME } from "@/config";

/**
 * Phase P2 — Company Profile context.
 *
 * On mount fetches `/api/settings/company/public` (unauthenticated) so the
 * login page + sidebar can render the correct brand name/logo before a
 * user is present. Authenticated consumers of the full profile use the
 * regular `api.get('/settings/company')` — this context intentionally
 * exposes only the public branding subset.
 */
const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const [company, setCompany] = useState({ name: APP_NAME, logo_file_id: null });
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/company/public");
      setCompany({
        name: (data && data.name) || APP_NAME,
        logo_file_id: (data && data.logo_file_id) || null,
      });
    } catch {
      // Keep default fallback — login page still renders.
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <CompanyContext.Provider value={{ company, loaded, refresh }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used inside <CompanyProvider>");
  return ctx;
}
